// Developer: Ankit Gupta
/**
 * Development-only auth provider that bypasses Keycloak.
 * Used when VITE_DEV_AUTH=true (no Keycloak running).
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { AuthContextValue, AuthUser } from './AuthProvider';
import { Navigate, useLocation } from 'react-router-dom';
import { useAccess, type Permission } from './useAccess';

const DEV_USER: AuthUser = {
  id: 'dev-user-001',
  username: 'devuser',
  email: 'dev@datapilot.local',
  firstName: 'Dev',
  lastName: 'User',
  fullName: 'Dev User',
  roles: ['datapilot_admin', 'datapilot_project_admin', 'datapilot_user'],
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function DevAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: true,
      isLoading: false,
      token: 'dev-token',
      user: DEV_USER,
      roles: DEV_USER.roles,
      logout: () => { window.location.href = '/'; },
      login: () => {},
      register: () => {},
    }),
    [],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useDevAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useDevAuth must be used inside <DevAuthProvider>');
  }
  return ctx;
}

export function DevProtectedRoute({
  children,
  requiredPermission,
}: {
  children: ReactNode;
  requiredRole?: string;
  requiredPermission?: Permission;
}) {
  // No Keycloak here, but the access list is the server's and applies in development too,
  // so a page closed to the person is closed the same way as in production.
  const access = useAccess();
  const location = useLocation();
  if (requiredPermission && access.isLoading) return null;
  if (requiredPermission && !access.can(requiredPermission)) {
    return <Navigate to="/unauthorized" state={{ from: location, requiredPermission }} replace />;
  }
  return <>{children}</>;
}
